import React from 'react';
import { useSelector } from 'react-redux';
import { FenderNotation } from '@fenderdigital/notation/types';
import Score from '@fenderdigital/notation/feature/score';
import WebAnalyticsAdapter from '@fenderdigital/notation/analytics/web-adapter';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import Flag from '../Flag';
import { trackEvent } from '../../lib/analytics';
import { RootState } from '../../redux-types/root.redux-types';
import { isLocked } from '../../utils/access-utils';
import { getFlag } from '../../lib/feature-flags';

interface NotationTabsProps {
  tabXML: string | FenderNotation.Score;
  isFullScreen?: boolean;
  toggleFullScreen: () => {};
}

const selector = (state: RootState) => ({
  course: state.courses.watching.currentCourse,
  lesson: state.courses.watching.currentLesson,
});

const NotationTabs: React.FC<NotationTabsProps> = ({ tabXML, isFullScreen, toggleFullScreen }) => {
  const { accessLevel: userAccessLevel } = usePlayUser();
  const { course, lesson } = useSelector(selector);
  const isFreemium = getFlag('isFreemium');

  if (!tabXML) {
    return null;
  }

  // eslint-disable-next-line
  // @ts-ignore
  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
  if (isIE11) {
    return <div className="futura-heavy pv2">Unable to render tablature.</div>;
  }

  const onUIEvent = (event: FenderNotation.UIEvent): void => {
    if (event.name === 'FullScreenToggle') {
      toggleFullScreen();
    }
  };

  const analyticsAdapter = new WebAnalyticsAdapter(trackEvent);
  analyticsAdapter.lessonMetadata = {
    id: lesson?.id ?? '',
    name: lesson?.title ?? '',
    slug: lesson?.slug ?? '',
    type: lesson?.type ?? '',
  };

  if (course) {
    analyticsAdapter.courseMetadata = {
      id: course.id,
      name: course.title,
      slug: course.slug,
      isLocked: isFreemium && isLocked(course['access-levels'], userAccessLevel),
    };
  }

  return (
    <div className="tab-render overflow-x-scroll overflow-x-hidden-l overflow-y-hidden">
      <Flag name="isZoomEnabled" fallBack={<Score tabData={tabXML} isFullScreen={isFullScreen} />}>
        <Score
          tabData={tabXML}
          analyticsAdapter={analyticsAdapter}
          isFullScreen={isFullScreen}
          isZoomEnabled
          onUIEvent={onUIEvent}
          hideFullScreenButton={isFullScreen}
        />
      </Flag>
    </div>
  );
};

export default NotationTabs;
